import React, { useState, useEffect, useMemo, useRef } from "react";

import { Box, Stack } from "@mui/material";
import { ReactFlowProvider } from "@xyflow/react";

import { useBuildLogs } from "src/domains/application/hooks/useBuildLogs";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";
import { useDeploymentStream } from "src/domains/application/hooks/useDeploymentStream";
import { useGitOpsStatus } from "src/domains/application/hooks/useGitOpsStatus";
import {
  usePipelineGraph,
  usePipelineGraphFromState,
} from "src/domains/application/hooks/usePipelineGraph";
import type { PipelineNodeData } from "src/domains/application/hooks/usePipelineGraph";
import { usePipelineRuns } from "src/domains/application/hooks/usePipelineRuns";
import { usePipelineRunsApi } from "src/domains/application/hooks/usePipelineRunsApi";
import { useVcsConnectionStatus } from "src/domains/application/hooks/useVcsConnectionStatus";

import { usePipelineActions } from "./hooks/usePipelineActions";
import { usePipelineState } from "./hooks/usePipelineState";
import { JobDetailPanel } from "./JobDetailPanel";
import { PhaseDetailView } from "./PhaseDetailView";
import { type PipelineTabProps } from "./pipeline-tab-helpers";
import { PipelineControls } from "./PipelineControls";
import { PipelineDAGView } from "./PipelineDAGView";
import { useEnvironments, useStacksForProject } from "./pipelineDataHooks";
import { PipelineEmptyState } from "./PipelineEmptyState";
import { PipelineErrorBanner } from "./PipelineErrorBanner";
import type { PipelineFilter } from "./PipelineFilterBar";
import { PipelineHeader } from "./PipelineHeader";
import { PipelineRunsList } from "./PipelineRunsList";
import { StagesSidebar, type JobInfo } from "./StagesSidebar";
import { usePipelineSearchParams } from "./usePipelineSearchParams";
import { WorkflowSidebar } from "./WorkflowSidebar";

// ── Main Component ────────────────────────────────────────────────────────────

function PipelineTabInner({
  alias,
  templateSlug,
  deployMode,
  orgAlias,
  vcsRepoUrl,
  status,
  projectAlias,
  onProvisionVcs,
  onPromote,
}: PipelineTabProps): React.ReactElement {
  const {
    events,
    loading,
    error,
    orgMismatch,
    connected,
    refetch,
    pipelineState,
    logBatches,
    pipelineRunRefreshSignal,
  } = useDeploymentStream(alias);

  // GitOps (ArgoCD) sync status
  const { gitopsStatus } = useGitOpsStatus(alias);

  // Environment sidebar data (project-level)
  const { environments, loading: _envLoading } = useEnvironments(
    orgAlias ?? "",
    projectAlias ?? "",
  );
  const { stacks: projectStacks, loading: _stacksLoading } =
    useStacksForProject(orgAlias ?? "", projectAlias ?? "");

  // URL-driven navigation state — survives page reload and browser back/forward
  const {
    run: selectedRunId,
    stage: selectedStage,
    job: selectedPanelService,
    env: selectedEnvSlug,
    setRun: setSelectedRunId,
    setStage: setSelectedStage,
    setJob: setSelectedPanelService,
    setEnv: setSelectedEnvSlug,
  } = usePipelineSearchParams();

  // Default: select the environment that matches the current stack (if URL has no env)
  useEffect(() => {
    if (environments.length === 0 || selectedEnvSlug) return;
    const matchingEnv = environments.find((e) =>
      projectStacks.some((s) => s.environmentId === e.id && s.alias === alias),
    );
    setSelectedEnvSlug(matchingEnv?.slug ?? environments[0]?.slug ?? null);
  }, [environments, projectStacks, alias, selectedEnvSlug, setSelectedEnvSlug]);

  // Resolve the stack for the selected environment
  const _envStack = useMemo(() => {
    const env = environments.find((e) => e.slug === selectedEnvSlug);
    if (!env) return null;
    return projectStacks.find((s) => s.environmentId === env.id) ?? null;
  }, [environments, projectStacks, selectedEnvSlug]);

  // VCS connection status (decoupled from build/deploy pipeline)
  const { vcsStatus } = useVcsConnectionStatus(orgAlias, vcsRepoUrl);

  // Detail panel state — useRef ensures WS-driven re-renders don't lose selection
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeData, setSelectedNodeData] =
    useState<PipelineNodeData | null>(null);
  const selectedNodeIdRef = useRef<string | null>(null);
  const selectedNodeDataRef = useRef<PipelineNodeData | null>(null);

  // Service selected within the group-build/group-deploy detail panel (level 2 navigation)
  const [selectedGroupService, setSelectedGroupService] = useState<
    string | null
  >(null);
  // selectedPanelService is now URL-driven (via usePipelineSearchParams)
  // selectedRunId is now URL-driven (via usePipelineSearchParams)
  // selectedStage is now URL-driven (via usePipelineSearchParams)

  // Phase jobs list (exported by PhaseDetailView, fed to StagesSidebar in jobs mode)
  const [phaseJobList, setPhaseJobList] = useState<JobInfo[]>([]);

  // Track last WS event timestamp for Live chip pulse animation
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);

  // Filter state
  const [activeFilter, _setActiveFilter] = useState<PipelineFilter>("all");

  // Track WS event arrivals for Live chip pulse
  useEffect(() => {
    if (events.length > 0) {
      setLastEventAt(Date.now());
    }
  }, [events]);

  // Retry/force-fail state
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [forceFailLoading, setForceFailLoading] = useState(false);
  const [forceFailError, setForceFailError] = useState<string | null>(null);
  const [redeployLoading, setRedeployLoading] = useState(false);
  const [redeployError, setRedeployError] = useState<string | null>(null);

  // Derive runs list from events (GitHub Actions style: click a run to see its DAG)
  const runs = usePipelineRuns(events);

  // API-driven pipeline runs — appear immediately on trigger (before events arrive)
  const { runs: apiRuns, refetch: refetchApiRuns } = usePipelineRunsApi(alias);

  // Refetch API runs when a pipeline_run WS event arrives (new run or status update)
  useEffect(() => {
    if (pipelineRunRefreshSignal > 0) {
      void refetchApiRuns();
    }
  }, [pipelineRunRefreshSignal, refetchApiRuns]);

  // ── Pipeline state derivations (extracted to usePipelineState) ──────────────
  const {
    templateServices,
    latestSagaId,
    scopedLogBatches,
    filteredEvents,
    effectivePipelineState,
    globalFilteredPipelineState,
    pipelineStatus,
    buildFailed,
    allCompleted,
    stages,
  } = usePipelineState({
    alias,
    templateSlug,
    status,
    deployMode,
    events,
    pipelineState,
    logBatches,
    selectedRunId,
    apiRuns,
    runs,
    vcsStatus,
    setSelectedRunId,
  });

  // For group-build/group-deploy nodes, use the selected service within the group.
  const isGroupNode =
    selectedNodeData?.nodeType === "group-build" ||
    selectedNodeData?.nodeType === "group-deploy" ||
    selectedNodeData?.nodeType === "group-ci";

  const effectiveServiceName = isGroupNode
    ? (selectedPanelService ??
      selectedGroupService ??
      selectedNodeData?.serviceName)
    : selectedNodeData?.serviceName;

  // Fetch service-specific logs when a service node is selected.
  const serviceLogs = useBuildLogs(alias, effectiveServiceName || undefined, {
    autoFetch: false,
    pollWhileActive:
      (selectedNodeData?.step === "build" ||
        selectedNodeData?.step === "deploy") &&
      selectedNodeData?.status === "in_progress",
    logBatches: scopedLogBatches,
    buildMode:
      deployMode === "rusha_managed" || deployMode === "rusha_build_ci_deploy"
        ? "rusha_managed"
        : "ci",
    sagaId: selectedRunId ?? latestSagaId ?? undefined,
  });

  // Only compute event-based graph when pipelineState has no rows (first-load fallback).
  // Pass empty array to the unused hook so Dagre layout runs once, not twice.
  const hasPipelineState = Boolean(
    effectivePipelineState && effectivePipelineState.length > 0,
  );

  const vcsStatusLabel =
    status === "provisioning" || status === "vcs_provisioned"
      ? "Connected — Provisioning"
      : undefined;

  const pipelineStateNodes = usePipelineGraphFromState(
    effectivePipelineState as PipelineStateStep[],
    templateServices,
    deployMode,
    vcsStatus.connected,
    vcsStatusLabel,
    vcsStatus.orgConnected,
    vcsRepoUrl,
  );
  const eventNodes = usePipelineGraph(
    hasPipelineState ? [] : filteredEvents,
    templateServices,
    deployMode,
    vcsStatus.connected,
    vcsStatusLabel,
    vcsStatus.orgConnected,
    vcsRepoUrl,
  );

  const { nodes, edges, serviceNames } = hasPipelineState
    ? pipelineStateNodes
    : eventNodes;

  // Rusha-owned build modes can retry/force-fail; CI-owned builds cannot.
  const isRushaOwnedBuild =
    deployMode === "rusha_managed" || deployMode === "rusha_build_ci_deploy";

  // ── Pipeline actions (extracted to usePipelineActions) ──────────────────────
  const {
    handleNodeClick,
    handleCloseDetail,
    handleJobsUpdate,
    handleBackToStages,
    handleRetryBuild,
    handleForceFail,
    handleRedeploy,
    handleStageSelect,
    handleBackToRuns,
    handleEnvSwitch,
    filteredNodeIds,
  } = usePipelineActions({
    alias,
    serviceNames,
    nodes,
    edges,
    activeFilter,
    selectedNodeIdRef,
    selectedNodeDataRef,
    selectedRunId,
    selectedStage,
    selectedNodeId,
    selectedNodeData,
    selectedPanelService,
    latestSagaId,
    effectiveServiceName,
    serviceLogs,
    setSelectedNodeId,
    setSelectedNodeData,
    setSelectedGroupService,
    setSelectedPanelService,
    setSelectedStage,
    setSelectedRunId,
    setSelectedEnvSlug,
    setPhaseJobList,
    setRetryLoading,
    setRetryError,
    setForceFailLoading,
    setForceFailError,
    setRedeployLoading,
    setRedeployError,
    refetch,
    refetchApiRuns,
  });

  // Navigation level: 1 = workflow+runs, 2 = stages+detail
  const navLevel = selectedRunId ? 2 : 1;

  return (
    <Stack spacing={2}>
      <PipelineHeader
        status={status}
        pipelineStatus={pipelineStatus}
        connected={connected}
        lastEventAt={lastEventAt}
        buildFailed={buildFailed}
        allCompleted={allCompleted}
        isRushaOwnedBuild={isRushaOwnedBuild}
        events={events}
        globalFilteredPipelineState={globalFilteredPipelineState}
        retryLoading={retryLoading}
        forceFailLoading={forceFailLoading}
        redeployLoading={redeployLoading}
        onRetryBuild={handleRetryBuild}
        onForceFail={handleForceFail}
        onRedeploy={handleRedeploy}
      />

      <PipelineErrorBanner
        retryError={retryError}
        forceFailError={forceFailError}
        redeployError={redeployError}
        onClearRetryError={() => setRetryError(null)}
        onClearForceFailError={() => setForceFailError(null)}
        onClearRedeployError={() => setRedeployError(null)}
      />

      <PipelineControls gitopsStatus={gitopsStatus} />

      <PipelineEmptyState
        loading={loading}
        error={error}
        orgMismatch={orgMismatch}
        status={status}
        vcsStatus={vcsStatus}
        onProvisionVcs={onProvisionVcs}
      />

      {/* 2-column level-based navigation:
          Level 1: WorkflowSidebar + RunsList
          Level 2: StagesSidebar + DAG/PhaseDetail */}
      {!loading && !error && (
        <Box sx={{ display: "flex", gap: 2, minHeight: 480 }}>
          {navLevel === 1 ? (
            /* Level 1: Workflows + Runs list */
            <>
              {projectAlias && environments.length > 0 && (
                <WorkflowSidebar
                  environments={environments}
                  selectedEnvSlug={selectedEnvSlug ?? ""}
                  onSelectEnv={handleEnvSwitch}
                  deployedEnvIds={
                    new Set(
                      projectStacks
                        .map((s) => s.environmentId)
                        .filter(
                          (id): id is number => id != null && id !== undefined,
                        ),
                    )
                  }
                  onPromote={onPromote ? (slug) => onPromote(slug) : undefined}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PipelineRunsList
                  stackAlias={alias}
                  deployMode={deployMode ?? ""}
                  orgAlias={orgAlias ?? ""}
                  selectedRunId={selectedRunId}
                  onSelectRun={setSelectedRunId}
                  refreshSignal={pipelineRunRefreshSignal}
                />
              </Box>
            </>
          ) : (
            /* Level 2: Stages sidebar + content */
            <>
              <StagesSidebar
                stages={stages}
                selectedStage={selectedStage}
                onSelectStage={handleStageSelect}
                onBackToRuns={handleBackToRuns}
                runLabel={
                  selectedRunId ? `Run ${selectedRunId.slice(0, 8)}` : undefined
                }
                phaseJobs={
                  (selectedStage === "build" || selectedStage === "deploy") &&
                  phaseJobList.length > 0
                    ? phaseJobList
                    : undefined
                }
                phaseLabel={
                  selectedStage === "build"
                    ? "Build"
                    : selectedStage === "deploy"
                      ? "Deploy"
                      : undefined
                }
                selectedJob={selectedPanelService}
                onSelectJob={setSelectedPanelService}
                onBackToStages={handleBackToStages}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {selectedStage === "summary" && (
                  <PipelineDAGView
                    nodes={nodes}
                    edges={edges}
                    onNodeClick={handleNodeClick}
                    filteredNodeIds={filteredNodeIds}
                    serviceCount={serviceNames.length}
                  />
                )}
                {selectedStage === "setup" && (
                  <JobDetailPanel
                    open={true}
                    onClose={() => {
                      handleCloseDetail();
                      setSelectedStage("summary");
                    }}
                    nodeId={selectedNodeId ?? "vcs-badge"}
                    nodeData={selectedNodeData}
                    events={filteredEvents}
                    buildLogJobs={serviceLogs.logs}
                    liveLogs={serviceLogs.liveLogs}
                    deployMode={deployMode}
                    qualityGates={serviceLogs.qualityGates}
                    templateServices={templateServices}
                    buildFailed={buildFailed}
                    retryLoading={retryLoading}
                    onRetryBuild={handleRetryBuild}
                    forceFailLoading={forceFailLoading}
                    onForceFail={handleForceFail}
                    selectedPanelService={selectedPanelService}
                    onServiceSelect={setSelectedPanelService}
                    pipelineRunId={serviceLogs.runId}
                    pipelineRunStatus={serviceLogs.runStatus}
                    pipelineRunConclusion={serviceLogs.runConclusion}
                    pipelineRunHtmlUrl={serviceLogs.runHtmlUrl}
                  />
                )}
                {(selectedStage === "build" || selectedStage === "deploy") && (
                  <PhaseDetailView
                    stackAlias={alias}
                    templateSlug={templateSlug}
                    deployMode={deployMode}
                    runId={selectedRunId ?? ""}
                    phase={selectedStage}
                    selectedJob={selectedPanelService}
                    onJobsUpdate={handleJobsUpdate}
                  />
                )}
                {/* Fallback: if a node was clicked from DAG but stage wasn't set */}
                {selectedNodeId !== null &&
                  selectedStage !== "summary" &&
                  selectedStage !== "setup" &&
                  selectedStage !== "build" &&
                  selectedStage !== "deploy" && (
                    <JobDetailPanel
                      open={true}
                      onClose={handleCloseDetail}
                      nodeId={selectedNodeId}
                      nodeData={selectedNodeData}
                      events={filteredEvents}
                      buildLogJobs={serviceLogs.logs}
                      liveLogs={serviceLogs.liveLogs}
                      deployMode={deployMode}
                      qualityGates={serviceLogs.qualityGates}
                      templateServices={templateServices}
                      buildFailed={buildFailed}
                      retryLoading={retryLoading}
                      onRetryBuild={handleRetryBuild}
                      forceFailLoading={forceFailLoading}
                      onForceFail={handleForceFail}
                      selectedPanelService={selectedPanelService}
                      onServiceSelect={setSelectedPanelService}
                      pipelineRunId={serviceLogs.runId}
                      pipelineRunStatus={serviceLogs.runStatus}
                      pipelineRunConclusion={serviceLogs.runConclusion}
                      pipelineRunHtmlUrl={serviceLogs.runHtmlUrl}
                    />
                  )}
              </Box>
            </>
          )}

          {/* VCS provisioning CTA — shown when org has VCS but app has no repo */}
        </Box>
      )}
    </Stack>
  );
}

export function PipelineTab(props: PipelineTabProps): React.ReactElement {
  return (
    <ReactFlowProvider>
      <PipelineTabInner {...props} />
    </ReactFlowProvider>
  );
}

export default PipelineTab;
